import { CARD_TYPES } from 'cards/constants/CardTypes';
import {
  DISABLED_MSG,
  MAGNET_DISABLED_MSG,
  BASE_PAPER_SHAPES,
  THICK_PAPER_TYPES,
} from 'cards/constants/CardLevelOptions';
import { PRODUCT_OPTION_DETAILS } from 'cards/constants/productOptionDetails';
import {
  CARD_SIZES,
  DISABLED_CUSTOM_FOIL_PAPER,
  DISABLED_FOLDED_PAPER,
  DISABLED_POSTCARD_PAPER,
  FOLDED_SIZES,
  SIZES_WITHOUT_SHAPES,
} from 'cards/constants/Cards';
import _maxBy from 'lodash/maxBy';
import _minBy from 'lodash/minBy';
import { getProductOptionDetailsKey } from 'cards/util/cardLevelOptionUtils';
import { ORIENTATION } from 'cards/types';

export const hasExtraCustomizableColors = options =>
  options.some(option => option.metadata?.['extra-customizable']);

export const getMaxOptionPrice = variations =>
  (variations && variations.length && _maxBy(variations, 'price_cents').price_cents) || 0;

export const getMinOptionPrice = variations =>
  (variations && variations.length && _minBy(variations, 'price_cents').price_cents) || 0;

export const getOptionPrice = (availableOptions, optionType, optionValue) => {
  const matchingOption = availableOptions?.[optionType]?.options.find(o => o.value === optionValue);
  return matchingOption?.price_delta_cents || 0;
};

const sectionIsHidden = (optionType, isStampFoilCard, cardType, options, activeSelection) => {
  const isLetterpress = activeSelection['printing-type'] === 'letterpress';
  const isVellumJacket = cardType === CARD_TYPES.vellumJacket;
  return (
    // Stamp foil STD cards do not display options for size or type
    (cardType === CARD_TYPES.std && isStampFoilCard && ['size', 'type'].includes(optionType)) ||
    // Stamp foil holiday cards do not display options for type
    (cardType === CARD_TYPES.holiday && isStampFoilCard && optionType === 'type') ||
    // Letterpress cards don't display foil-color options
    (isLetterpress && optionType === 'foil-color') ||
    // Letterpress cards don't display type options
    (isLetterpress && optionType === 'type') ||
    // Postcard size -> no custom foil options
    (optionType === 'foil-color' && activeSelection.size === 'postcard') ||
    // Empty options aren't displayed
    options.length < 1 ||
    // Hide single options EXCEPT extra-customizable cards, lettepress paper-type & paper add-ons size
    (options.length === 1 &&
      !options[0].metadata?.['extra-customizable'] &&
      !(isLetterpress && optionType === 'paper-type') &&
      !(isVellumJacket && optionType === 'size')) ||
    // Hide the shape section for a card when only square corners are enabled
    (optionType === 'silhouette' && options.filter(option => !option.hidden).length === 1)
  );
};

export const getTooltipMessagePerOption = (optionType, activeSize, isMagnet = false) => {
  if (optionType === 'paper-type' && activeSize === CARD_SIZES.postcard) {
    /*
     * A specific message can be added for a specfic property
     * in the DISABLED_MSG object (i.e. pearlized is out of stock)
     * see src/cards/components/CardDetail/ToggleButton/ToggleButton.jsx for usage
     *
     * But for the postcard size, the specific messaging for paper-type does not apply
     * since the paper type(s) concerned are not available by default.
     * Hence enforcing the tooltip message to consider only the default case for messaging.
     */

    return DISABLED_MSG[optionType].default;
  }
  return isMagnet ? MAGNET_DISABLED_MSG[optionType] : DISABLED_MSG[optionType];
};

const disableOptions = (allOptions, isValueDisabled) => {
  return allOptions.map(o => ({
    ...o,
    enabled: o.enabled && !isValueDisabled(o.value),
  }));
};

const applyFrontEndOptionFilters = (allOptions, activeSelection, key, isStampFoilCard) => {
  // No use filtering unless the selection is available
  if (!activeSelection) return allOptions;

  const {
    size: activeSize,
    silhouette: activeShape,
    foil: activeFoilType,
    'paper-type': activePaperType,
  } = activeSelection;

  switch (key) {
    case 'silhouette':
      // Only leave the square shape for certain sizes
      if (SIZES_WITHOUT_SHAPES.includes(activeSize)) {
        return allOptions.filter(option => option.value === 'square');
      }
      // Remove non-basic shapes if a thicker paper type is selected
      if (THICK_PAPER_TYPES.includes(activePaperType)) {
        return allOptions.filter(option => BASE_PAPER_SHAPES.includes(option.value));
      }
      break;
    case 'foil-color':
      // Disable custom foil colors when an unsupported paper type is selected
      if (!isStampFoilCard && DISABLED_CUSTOM_FOIL_PAPER.includes(activePaperType)) {
        return disableOptions(allOptions, v => v !== 'none');
      }
      break;
    case 'paper-type': {
      let updatedOptions = allOptions;
      // Disable certain paper types when custom foil is active
      if (activeFoilType === 'custom') {
        updatedOptions = disableOptions(updatedOptions, v =>
          DISABLED_CUSTOM_FOIL_PAPER.includes(v)
        );
      }

      // Disable thicker paper types when a non-basic shape is selected
      if (activeShape && !BASE_PAPER_SHAPES.includes(activeShape)) {
        updatedOptions = disableOptions(updatedOptions, v => THICK_PAPER_TYPES.includes(v));
      }

      // Disable certain paper-types when postcard or folded sizes are selected
      if (activeSize === 'postcard') {
        updatedOptions = disableOptions(updatedOptions, v => DISABLED_POSTCARD_PAPER.includes(v));
      } else if (FOLDED_SIZES.includes(activeSize)) {
        updatedOptions = disableOptions(updatedOptions, v => DISABLED_FOLDED_PAPER.includes(v));
      }
      return updatedOptions;
    }
    default:
      break;
  }

  // For all other option keys, no need to filter
  return allOptions;
};

export const enrichOptions = ({
  key,
  options,
  cardType,
  isStampFoilCard,
  cardOrientation = ORIENTATION.portrait, // only needed to determine folded card size option
  activeSelection,
  useFrontEndFilters = false,
}) => {
  // If options aren't defined or are empty, immediately return an empty array.
  if (!options?.length) return [];

  let allOptions = options;

  // We don't have hardcoded details for theme colors (there are too many!), but we show FE-only rainbow swatch if there is a SINGLE extra-customizable color
  if (key === 'color' && options.length === 1 && hasExtraCustomizableColors(options)) {
    const extraCustomizableOption = options[0];
    const rainbowSwatchOption = {
      ...extraCustomizableOption,
      metadata: {
        ...extraCustomizableOption.metadata,
        showRainbowSwatch: true,
      },
    };
    allOptions = [rainbowSwatchOption];
  }

  // Hide disabled options in certain cases
  if (key === 'silhouette' || (key === 'foil-color' && isStampFoilCard)) {
    allOptions = allOptions.filter(option => option.enabled);
  }

  // Until we finish updating the customization & samples flows to refetch available options whenever
  // the variation changes, we'll need to manually disable some options as the active selection changes.
  if (useFrontEndFilters) {
    allOptions = applyFrontEndOptionFilters(allOptions, activeSelection, key, isStampFoilCard);
  }

  if (sectionIsHidden(key, isStampFoilCard, cardType, allOptions, activeSelection)) {
    return [];
  }

  return allOptions.map(option => {
    const productOptionKey = getProductOptionDetailsKey(
      key,
      option.value,
      cardType,
      cardOrientation
    );
    const productOptionDetails = PRODUCT_OPTION_DETAILS[productOptionKey];
    return {
      ...option,
      ...productOptionDetails,
      disabled: !option.enabled,
    };
  });
};
